import { useEffect, useRef, useState } from "react";
import { Zoom, toast } from "react-toastify";
import { ReturnViewMode } from "..";
import { returnToastStyle } from "../eQueueUtils";

const makeElapsedTimer = (
	startTime: string | undefined,
	setter: (v: string | undefined) => void
): ReturnType<typeof setInterval> | undefined => {
	if ( !startTime ) { setter(undefined); return undefined; }
	const pad = (n: number) => String(n).padStart(2, "0");
	const tick = () => {
		const diffSec = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
		const h = Math.floor(diffSec / 3600);
		const m = Math.floor((diffSec % 3600) / 60);
		const s = diffSec % 60;
		setter( h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}` );
	};
	tick();
	return setInterval(tick, 1000);
};

interface UseReturnTimersParams {
	isReturnQueue: boolean;
	viewMode: ReturnViewMode | undefined;
	pauseStartTime: string | undefined;
	ticket: { number?: string; inviteTime?: string } | undefined;
}

export const useReturnTimers = ({ isReturnQueue, viewMode, pauseStartTime, ticket }: UseReturnTimersParams) => {
	const [ pauseElapsed, setPauseElapsed ] = useState<string | undefined>(undefined);
	useEffect(() => {
		if ( !isReturnQueue || viewMode !== "pause" ) { setPauseElapsed(undefined); return; }
		const id = makeElapsedTimer(pauseStartTime, setPauseElapsed);
		return () => { if (id) clearInterval(id); };
	}, [ isReturnQueue, viewMode, pauseStartTime ]);

	const [ callElapsed, setCallElapsed ] = useState<string | undefined>(undefined);
	const noShowToastFired = useRef(false);
	useEffect(() => {
		if ( !isReturnQueue || viewMode !== "called" ) {
			setCallElapsed(undefined);
			noShowToastFired.current = false;
			return;
		}
		const timerId = makeElapsedTimer(ticket?.inviteTime, setCallElapsed);
		const inviteTime = ticket?.inviteTime;
		const ticketNumber = ticket?.number;
		let toastId: ReturnType<typeof setTimeout> | undefined;
		if ( inviteTime ) {
			const remaining = 7 * 60 * 1000 - (Date.now() - new Date(inviteTime).getTime());
			if ( remaining > 0 ) {
				toastId = setTimeout(() => {
					if ( noShowToastFired.current ) return;
					noShowToastFired.current = true;
					toast.success(`Посетитель с талоном ${ticketNumber} не явился`, {
						position: "top-center",
						autoClose: 10000,
						hideProgressBar: true,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
						transition: Zoom,
						closeButton: false,
						style: returnToastStyle
					});
				}, remaining);
			}
		}
		return () => {
			if (timerId) clearInterval(timerId);
			if (toastId) clearTimeout(toastId);
		};
	}, [ isReturnQueue, viewMode, ticket?.inviteTime ]);

	return { pauseElapsed, callElapsed };
};
